import React, { useMemo, useState } from 'react';
import type {
  AiModelConfig,
  AiModelOption,
  AiPromptTemplate,
  AiTestJobOption,
  AiTestProject,
  AiTestSourceOfTruthOption,
} from './types';
import { createTesterRun } from './api';
import JobPicker from './JobPicker';
import ModelPicker from './ModelPicker';
import PromptPicker from './PromptPicker';
import SourceOfTruthPicker from './SourceOfTruthPicker';

interface TensionFormProps {
  projects: AiTestProject[];
  models: AiModelOption[];
  prompts: AiPromptTemplate[];
  onPromptsChanged: () => Promise<void> | void;
  onRunStarted: () => Promise<void> | void;
}

const TensionForm: React.FC<TensionFormProps> = ({
  projects,
  models,
  prompts,
  onPromptsChanged,
  onRunStarted,
}) => {
  const [projectId, setProjectId] = useState<number | ''>('');
  const [sourceOfTruth, setSourceOfTruth] = useState<AiTestSourceOfTruthOption | null>(null);
  const [job, setJob] = useState<AiTestJobOption | null>(null);
  const [useOpportunitySpace, setUseOpportunitySpace] = useState(true);
  const [useMarket, setUseMarket] = useState(true);
  const [appendToProject, setAppendToProject] = useState(true);
  const [modelId, setModelId] = useState('');
  const [modelConfig, setModelConfig] = useState<AiModelConfig>({});
  const [prompt, setPrompt] = useState<AiPromptTemplate | null>(null);
  const [promptContent, setPromptContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const project = projects.find((p) => p.id === projectId);
  const tensionPrompts = useMemo(
    () => prompts.filter((p) => !p.step_key || p.step_key === ''),
    [prompts]
  );

  const handleRun = async () => {
    if (!project) {
      setError('Select a project first.');
      return;
    }
    if (!sourceOfTruth) {
      setError('Select a source of truth file.');
      return;
    }
    if (!job) {
      setError('Select a job to be done.');
      return;
    }
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    const variableKeys = ['source_of_truth', 'jtbd'];
    if (useOpportunitySpace) variableKeys.push('opportunity_space');
    if (useMarket) variableKeys.push('market');
    const inline = promptContent.trim();
    try {
      const run = await createTesterRun({
        project_id: project.id,
        generator_key: 'tension',
        variable_keys: variableKeys,
        model_id: modelId || undefined,
        model_config: modelConfig,
        source_of_truth: {
          kind: sourceOfTruth.kind,
          job_id: sourceOfTruth.job_id,
          report_url: sourceOfTruth.report_url,
          country: sourceOfTruth.country,
          label: sourceOfTruth.label,
        },
        job_to_be_done: job.text,
        prompt_inline: inline || undefined,
        prompt_id: inline ? undefined : prompt?.id || undefined,
        blob_filename: inline || prompt?.id ? undefined : prompt?.blob_filename || undefined,
        append_to_project: appendToProject,
      });
      setSuccess(`Run #${run.id} started (${run.status}).`);
      await onRunStarted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start the run');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-[12px] font-medium text-[#626262] mb-1">Project</label>
        <select
          value={projectId}
          onChange={(e) => {
            setProjectId(e.target.value ? Number(e.target.value) : '');
            setSourceOfTruth(null);
            setJob(null);
          }}
          className="w-full rounded-md border border-[#D1D1D1] px-3 py-2 text-sm"
        >
          <option value="">Select a project</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>

      <SourceOfTruthPicker
        projectId={projectId}
        value={sourceOfTruth}
        onChange={(next) => {
          setSourceOfTruth(next);
          if (!next || next.country !== sourceOfTruth?.country) {
            setJob(null);
          }
        }}
        disabled={submitting}
      />

      {sourceOfTruth && (
        <p className="text-[12px] text-[#626262]">
          Market: <span className="font-medium text-[#0C0B0B]">{sourceOfTruth.country}</span>
        </p>
      )}

      <JobPicker
        projectId={projectId}
        market={sourceOfTruth?.country || ''}
        value={job}
        onChange={setJob}
        disabled={submitting}
      />

      <div>
        <p className="text-[12px] font-medium text-[#626262] mb-2">Prompt variables</p>
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm text-[#8A8A8A]">
            <input type="checkbox" checked disabled />
            Source of Truth
          </label>
          <label className="flex items-center gap-2 text-sm text-[#8A8A8A]">
            <input type="checkbox" checked disabled />
            Job to be done
          </label>
          <label className="flex items-center gap-2 text-sm text-[#4A4A4A]">
            <input
              type="checkbox"
              checked={useOpportunitySpace}
              onChange={(e) => setUseOpportunitySpace(e.target.checked)}
            />
            Opportunity Space
          </label>
          <label className="flex items-center gap-2 text-sm text-[#4A4A4A]">
            <input
              type="checkbox"
              checked={useMarket}
              onChange={(e) => setUseMarket(e.target.checked)}
            />
            Market
            {sourceOfTruth && (
              <span className="text-[12px] text-[#8A8A8A]">({sourceOfTruth.country})</span>
            )}
          </label>
        </div>
      </div>

      <ModelPicker
        models={models}
        modelId={modelId}
        onModelId={setModelId}
        config={modelConfig}
        onConfig={setModelConfig}
        generatorKey="tension"
        disabled={submitting}
      />

      <PromptPicker
        label="Tension prompt"
        generatorKey="tension"
        stepKey=""
        prompts={tensionPrompts}
        selectedId={prompt?.id ?? null}
        blobFilename={prompt?.blob_filename || ''}
        onSelect={setPrompt}
        onContentChange={setPromptContent}
        onPromptsChanged={onPromptsChanged}
        disabled={submitting}
      />

      <label className="flex items-center gap-2 text-sm text-[#4A4A4A]">
        <input
          type="checkbox"
          checked={appendToProject}
          onChange={(e) => setAppendToProject(e.target.checked)}
        />
        Add tensions to the project workspace
      </label>

      {error && <p className="text-sm text-red-600">{error}</p>}
      {success && <p className="text-sm text-emerald-700">{success}</p>}

      <button
        type="button"
        onClick={() => void handleRun()}
        disabled={submitting || !project || !sourceOfTruth || !job}
        className="h-9 px-4 rounded-md bg-[#3da3d4] text-white text-sm font-medium disabled:opacity-50"
      >
        {submitting ? 'Starting…' : 'Run Tension'}
      </button>
    </div>
  );
};

export default TensionForm;
